/**
 * SearchService
 *
 * @author Copyright (c) 2021 Salesforce.com.
 * @ownership Appexchange Intelligence
 */
import { RECOMMENDATION_SERVICE_URL, SPONSERED_RESULTS_LIMIT } from '~/modules/config';
import { getPreferredLanguage } from '~/modules/helpers/languageHelper';
import { handleException } from '~/modules/utilities/error_utilities';
import { HttpRequestConfig, gatewayParamsSerializer, http } from '~/modules/utilities/http';
import { SearchResult } from '../../schemas/search-schema-v1';
import { buildSearchResults } from '../helpers/searchServiceHelper';
import { FilterParams } from '../stores/searchStore';

export interface Suggestion {
    suggestedKeyword: string;
    highlighted: string;
}

export interface SuggestionsResult {
    suggestions: Suggestion[];
}

type SearchApiQueryParams = {
    keyword?: string;
    type: string;
    page: string;
    pageSize: string;
    showSponsored?: string;
    sponsoredCount?: string;
    sort?: string;
    debugRankings?: string;
    language?: string;
    searchQueryId?: string;
    prevSearchQueryId?: string;
} & { [K in keyof FilterParams]: string };

const getApiEndpoint = (): string => {
    return RECOMMENDATION_SERVICE_URL;
};

const getSearchEndpoint = (): string => {
    return `${getApiEndpoint()}/v3/listings`;
};

const getSearchTermSuggestionsEndpoint = (): string => {
    return `${getApiEndpoint()}/v1/suggestions`;
};

const getSearchResults = async (
    keyword: string | undefined,
    type: string,
    page: number,
    pageSize: number,
    filterParams: FilterParams = {},
    sort: string | undefined = undefined,
    rankingInformation = false,
    searchQueryId?: string,
    prevSearchQueryId?: string,
): Promise<SearchResult> => {
    const params: SearchApiQueryParams = {
        type: type ? type : 'apps',
        page: page.toString(),
        pageSize: pageSize.toString(),
        language: getPreferredLanguage(),
    };

    if (SPONSERED_RESULTS_LIMIT) {
        params.sponsoredCount = String(SPONSERED_RESULTS_LIMIT);
    }

    if (keyword && keyword.length > 0) {
        params.keyword = keyword;
    }

    if (sort) {
        params.sort = sort;
    }

    if (rankingInformation) {
        params.debugRankings = String(rankingInformation);
    }

    if (searchQueryId) {
        params.searchQueryId = searchQueryId;
    }

    if (prevSearchQueryId) {
        params.prevSearchQueryId = prevSearchQueryId;
    }

    (Object.keys(filterParams) as (keyof FilterParams)[]).forEach((param) => {
        const paramValue = filterParams[param];
        if (paramValue !== null && paramValue !== undefined) {
            if (Array.isArray(paramValue)) {
                params[param] = paramValue.join(';');
            } else {
                params[param] = paramValue;
            }
        }
    });

    const httpConfig: HttpRequestConfig = {
        params,
        paramsSerializer: gatewayParamsSerializer,
    };

    const response = await http.get(getSearchEndpoint(), httpConfig);
    return buildSearchResults(response)[0];
};

const getQueryCount = async (keyword: string, type: string): Promise<number> => {
    const params: SearchApiQueryParams = {
        keyword,
        type,
        page: '1',
        pageSize: '0', // this makes the response lighter, because we will not get any listings
        language: getPreferredLanguage(),
    };

    const httpConfig: HttpRequestConfig = {
        params,
        paramsSerializer: gatewayParamsSerializer,
    };
    const response = await http.get(getSearchEndpoint(), httpConfig);
    if (response.data) {
        return response.data.totalCount;
    }
    return 0;
};

const getSearchTermSuggestions = async (keyword: string): Promise<SuggestionsResult> => {
    try {
        const encodedKeyword = encodeURIComponent(keyword);
        const language = getPreferredLanguage();
        const response = await http.get(
            `${getSearchTermSuggestionsEndpoint()}?keyword=${encodedKeyword}&language=${language}`,
        );
        return response.data;
    } catch (error) {
        //TODO: handle errors so we don't cache empty data
        handleException(error, 'searchService.getSearchTermSuggestions');
        return { suggestions: [] };
    }
};

export { getQueryCount, getSearchResults, getSearchTermSuggestions };
